import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../../data/notification";
import useT from "../../i18n/useT";
import core from "../../core";

const statusStyle = (status) => {
    if (status === "active") return { background: "#2f80ed", color: "#fff" };
    if (status === "paid") return { background: "#34c38f", color: "#fff" };
    if (status === "rejected") return { background: "#dc3545", color: "#fff" };
    return { background: "#ff9f0a", color: "#fff" };
};

const Financing = () => {
    const t = useT();
    const dispatch = useDispatch();
    const role = useSelector((state) => state.user.role);
    const isStaff = role === "admin" || role === "moderator";

    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updates, setUpdates] = useState(0);
    const [form, setForm] = useState({ amount: "", vin: "", note: "" });
    const [saving, setSaving] = useState(false);

    // Approve modal state
    const [approveFor, setApproveFor] = useState(null); // request row
    const [terms, setTerms] = useState({ interest_rate: "", term_months: "" });
    // Payment modal state
    const [payFor, setPayFor] = useState(null); // loan row
    const [payForm, setPayForm] = useState({ amount: "", note: "" });
    // Payments viewer
    const [paymentsFor, setPaymentsFor] = useState(null);
    const [payments, setPayments] = useState([]);

    const notify = (text, type) => {
        dispatch(notificationActions.changeNotification({ show: true, text, type }));
        setTimeout(() => dispatch(notificationActions.changeNotification({ show: false, text: "", type: "" })), 4000);
    };

    useEffect(() => {
        setLoading(true);
        axios.get("/api/financing-requests")
            .then((res) => { setRequests(res.data.data || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [updates]);

    const submit = (e) => {
        e.preventDefault();
        if (form.amount === "" || Number(form.amount) <= 0) return;
        setSaving(true);
        axios.post("/api/financing-requests", form)
            .then((res) => { notify(res.data.message.en, "success-notification"); setForm({ amount: "", vin: "", note: "" }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"))
            .finally(() => setSaving(false));
    };

    const reject = (id) => {
        axios.put(`/api/financing-requests/${id}`, { status: "rejected" })
            .then((res) => { notify(res.data.message.en, "success-notification"); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const confirmApprove = () => {
        axios.put(`/api/financing-requests/${approveFor.id}`, {
            status: "active",
            interest_rate: terms.interest_rate || 0,
            term_months: terms.term_months || 0,
        })
            .then((res) => { notify(res.data.message.en, "success-notification"); setApproveFor(null); setTerms({ interest_rate: "", term_months: "" }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const confirmPayment = () => {
        if (payForm.amount === "" || Number(payForm.amount) <= 0) return;
        axios.post(`/api/financing-requests/${payFor.id}/payments`, payForm)
            .then((res) => { notify(res.data.message.en, "success-notification"); setPayFor(null); setPayForm({ amount: "", note: "" }); setUpdates((u) => u + 1); })
            .catch((err) => notify(err.response?.data?.message?.en || "Error", "error-notification"));
    };

    const openPayments = (loan) => {
        setPaymentsFor(loan);
        setPayments([]);
        axios.get(`/api/financing-requests/${loan.id}/payments`)
            .then((res) => setPayments(res.data.data || []))
            .catch(() => {});
    };

    const statusLabel = (s) => t(`financing.${s}`);
    const money = (n) => "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="page-content-main-container">
            <h2 className="page-title">{t("financing.title")}</h2>

            {!isStaff && (
                <div style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "18px", marginBottom: "20px", maxWidth: "640px" }}>
                    <h3 style={{ fontSize: "15px", marginBottom: "14px" }}>{t("financing.newRequest")}</h3>
                    <form onSubmit={submit} style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 120px" }}>
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.amount")}</label>
                            <input type="number" step="0.01" min="0" className="form-control" value={form.amount} onChange={(e) => setForm((p) => ({ ...p, amount: e.target.value }))} required />
                        </div>
                        <div style={{ flex: "1 1 140px" }}>
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.vin")}</label>
                            <input type="text" className="form-control" value={form.vin} onChange={(e) => setForm((p) => ({ ...p, vin: e.target.value }))} />
                        </div>
                        <div style={{ flex: "2 1 180px" }}>
                            <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.note")}</label>
                            <input type="text" className="form-control" value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} />
                        </div>
                        <button type="submit" className="btn btn-primary" disabled={saving}>{t("financing.submit")}</button>
                    </form>
                </div>
            )}

            <h3 style={{ fontSize: "15px", margin: "6px 0 12px" }}>{isStaff ? t("financing.allLoans") : t("financing.myLoans")}</h3>

            {loading ? null : requests.length === 0 ? (
                <div style={{ opacity: 0.6 }}>{t("financing.none")}</div>
            ) : (
                <div style={{ overflowX: "auto" }}>
                    <table className="table" style={{ width: "100%" }}>
                        <thead>
                            <tr>
                                {isStaff && <th>{t("financing.dealer")}</th>}
                                <th>{t("financing.amount")}</th>
                                <th>{t("financing.vin")}</th>
                                <th>{t("financing.interestRate")}</th>
                                <th>{t("financing.totalPayable")}</th>
                                <th>{t("financing.outstanding")}</th>
                                <th>{t("financing.dueDate")}</th>
                                <th>{t("financing.status")}</th>
                                <th>{t("financing.date")}</th>
                                <th>{t("financing.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.map((r) => (
                                <tr key={r.id}>
                                    {isStaff && <td>{r.name ? `${r.name} ${r.surname || ""}` : r.username || r.user_id}</td>}
                                    <td>{money(r.amount)}</td>
                                    <td>{r.vin || "—"}</td>
                                    <td>{r.status === "pending" ? "—" : `${Number(r.interest_rate) || 0}%`}</td>
                                    <td>{r.status === "pending" || r.status === "rejected" ? "—" : money(r.total_payable)}</td>
                                    <td>{r.status === "active" || r.status === "paid" ? money(r.outstanding) : "—"}</td>
                                    <td>{r.due_date ? core.formdatDate(r.due_date, "dd-mm-yyyy") : "—"}</td>
                                    <td><span style={{ ...statusStyle(r.status), padding: "2px 10px", borderRadius: "12px", fontSize: "12px" }}>{statusLabel(r.status)}</span></td>
                                    <td>{r.created_at ? core.formdatDate(r.created_at, "dd-mm-yyyy") : "—"}</td>
                                    <td>
                                        <span style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                            {isStaff && r.status === "pending" && (
                                                <>
                                                    <button className="btn btn-sm btn-success" onClick={() => { setApproveFor(r); setTerms({ interest_rate: "", term_months: "" }); }}>{t("financing.approve")}</button>
                                                    <button className="btn btn-sm btn-danger" onClick={() => reject(r.id)}>{t("financing.reject")}</button>
                                                </>
                                            )}
                                            {isStaff && r.status === "active" && (
                                                <button className="btn btn-sm btn-primary" onClick={() => { setPayFor(r); setPayForm({ amount: "", note: "" }); }}>{t("financing.recordPayment")}</button>
                                            )}
                                            {(r.status === "active" || r.status === "paid") && (
                                                <button className="btn btn-sm btn-outline-secondary" onClick={() => openPayments(r)}>{t("financing.viewPayments")}</button>
                                            )}
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {approveFor && (
                <div onClick={() => setApproveFor(null)} style={backdrop}>
                    <div onClick={(e) => e.stopPropagation()} style={modal}>
                        <h3 style={{ fontSize: "16px", marginBottom: "4px" }}>{t("financing.approveTitle")}</h3>
                        <div style={{ fontSize: "13px", opacity: 0.7, marginBottom: "14px" }}>{t("financing.amount")}: {money(approveFor.amount)}</div>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.interestRate")}</label>
                        <input type="number" step="0.01" min="0" className="form-control" value={terms.interest_rate} onChange={(e) => setTerms((p) => ({ ...p, interest_rate: e.target.value }))} style={{ marginBottom: "10px" }} />
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.termMonths")}</label>
                        <input type="number" min="0" step="1" className="form-control" value={terms.term_months} onChange={(e) => setTerms((p) => ({ ...p, term_months: e.target.value }))} style={{ marginBottom: "16px" }} />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setApproveFor(null)}>{t("financing.cancel")}</button>
                            <button className="btn btn-sm btn-success" onClick={confirmApprove}>{t("financing.confirm")}</button>
                        </div>
                    </div>
                </div>
            )}

            {payFor && (
                <div onClick={() => setPayFor(null)} style={backdrop}>
                    <div onClick={(e) => e.stopPropagation()} style={modal}>
                        <h3 style={{ fontSize: "16px", marginBottom: "4px" }}>{t("financing.recordPayment")}</h3>
                        <div style={{ fontSize: "13px", opacity: 0.7, marginBottom: "14px" }}>{t("financing.outstanding")}: {money(payFor.outstanding)}</div>
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.amount")}</label>
                        <input type="number" step="0.01" min="0" className="form-control" value={payForm.amount} onChange={(e) => setPayForm((p) => ({ ...p, amount: e.target.value }))} style={{ marginBottom: "10px" }} />
                        <label style={{ fontSize: "12px", opacity: 0.7 }}>{t("financing.note")}</label>
                        <input type="text" className="form-control" value={payForm.note} onChange={(e) => setPayForm((p) => ({ ...p, note: e.target.value }))} style={{ marginBottom: "16px" }} />
                        <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setPayFor(null)}>{t("financing.cancel")}</button>
                            <button className="btn btn-sm btn-success" onClick={confirmPayment}>{t("financing.confirm")}</button>
                        </div>
                    </div>
                </div>
            )}

            {paymentsFor && (
                <div onClick={() => setPaymentsFor(null)} style={backdrop}>
                    <div onClick={(e) => e.stopPropagation()} style={modal}>
                        <h3 style={{ fontSize: "16px", marginBottom: "14px" }}>{t("financing.payments")}</h3>
                        {payments.length === 0 ? (
                            <div style={{ opacity: 0.6, marginBottom: "16px" }}>{t("financing.noPayments")}</div>
                        ) : (
                            <table className="table" style={{ width: "100%", marginBottom: "16px" }}>
                                <thead><tr><th>{t("financing.date")}</th><th>{t("financing.amount")}</th><th>{t("financing.note")}</th></tr></thead>
                                <tbody>
                                    {payments.map((p) => (
                                        <tr key={p.id}>
                                            <td>{p.created_at ? core.formdatDate(p.created_at, "dd-mm-yyyy") : "—"}</td>
                                            <td>{money(p.amount)}</td>
                                            <td>{p.note || "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setPaymentsFor(null)}>{t("financing.cancel")}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const backdrop = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modal = { background: "var(--card)", border: "1px solid var(--border)", borderRadius: "12px", padding: "22px", width: "min(440px, 92vw)", maxHeight: "86vh", overflowY: "auto" };

export default Financing;
